/**
 * Render-feature switches, readable from the URL.
 *
 * These exist because a shader that will not compile takes the whole world with
 * it — Babylon silently skips any mesh whose material failed, so the game goes
 * black behind a perfectly working HUD. Diagnosing that from a console line
 * number is slow; flipping one feature per reload is fast.
 *
 * `?safe=1` turns off everything that adds defines to the generated material
 * shader, leaving the plainest path Babylon has. If the world appears in safe
 * mode, the culprit is one of these; if it does not, the problem is in core
 * StandardMaterial or the engine itself.
 *
 *   ?safe=1       every switch below off
 *   ?fog=0        no scene fog        (drops FOG defines)
 *   ?shadows=0    no shadow generator (drops SHADOW* defines)
 *   ?specular=0   flat unlit-ish materials
 *   ?pixelate=0   no post-process
 *   ?glow=0       no bloom on emissive surfaces
 *   ?hitboxes=1   draw the invisible click colliders as wireframes
 *   ?px=1|2|3…    art-pixel size, overriding `PIXEL_SIZE` — see `pixelSizeOverride`
 *   ?skinned=0    the box rig instead of the painted .glb bodies — **on by default** since 2026-09-25
 *   ?props=0      the procedural stalls, lamps and markers instead of the generated props
 *   ?grade=0      no colour grade or vignette
 *   ?outline=0    the old per-mesh hover outline instead of the silhouette
 *   ?mobskins=0   monsters as the box rig while players keep their bodies (ADR 0019)
 */

function flag(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function off(name: string): boolean {
  const v = flag(name);
  return v === '0' || v === 'false' || v === 'off';
}

/**
 * An art-pixel size from the URL, or `null` for the shipped `PIXEL_SIZE`.
 *
 * **This exists because the harness cannot answer the question it answers.** How chunky the
 * world should be is judged by looking at it, and two things stop that happening headlessly:
 * `DEFAULT_SETTINGS.pixelate` is **false**, so the pass is not even running unless a player
 * turns it on, and the only browser this repo drives runs **SwiftShader**. A comparison run
 * across `PIXEL_SIZE` 3, 2 and 1 came back byte-identical — mean horizontal run 1.40 px in all
 * three — for the first of those reasons, and would not have been trustworthy for the second.
 * So the comparison belongs on a real GPU with the setting on, and a rebuild between each look
 * is the friction that stops it happening at all.
 *
 * Clamped to 1–8 and ignored when it is not a number: this rides on a query string a player
 * can type, and `pixelSize` reaching the shader as `0` or `NaN` makes `cells` infinite and the
 * screen one flat clamped texel — which looks like a driver fault rather than a typo. The
 * shader guards the same value again for the same reason.
 *
 * It deliberately does **not** touch `MIN_OUTLINE_WIDTH`. That constant is the floor a
 * world-space line must clear to survive downsampling, `entities.test.ts` asserts against it,
 * and a URL flag must not be able to move a tested invariant. Leaving it at the shipped
 * `PIXEL_SIZE` is the conservative direction anyway — a *smaller* art pixel makes the existing
 * outlines wider than they need to be, never thinner.
 */
/**
 * `?zoom=N`: pull the camera in N× (1 → 6), for a browser check that needs to *look* at a
 * model. The gameplay camera is `CAMERA_RADIUS` and nothing else in the client reads this;
 * the HUD's screen projections are derived from the camera each frame, so they follow.
 * Absent, malformed or out of range means 1 — the game as shipped.
 */
export function cameraZoomOverride(): number {
  const raw = flag('zoom');
  if (raw === null) return 1;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  return Math.min(6, Math.max(1, n));
}

export function pixelSizeOverride(): number | null {
  const raw = flag('px');
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(8, Math.max(1, Math.round(n)));
}

/**
 * True when the click colliders should be drawn.
 *
 * They are invisible by design — nothing drawn is pickable and nothing pickable
 * is drawn — which makes "why did that click miss?" impossible to answer by
 * looking at the screen. This turns them on.
 */
export function showHitboxes(): boolean {
  const v = flag('hitboxes');
  return v === '1' || v === 'true' || v === 'on';
}

/** True when every optional render feature should be disabled. */
export function safeMode(): boolean {
  const v = flag('safe');
  return v === '1' || v === 'true' || v === 'on';
}

export const renderFeatures = {
  get fog(): boolean {
    return !safeMode() && !off('fog');
  },
  get shadows(): boolean {
    return !safeMode() && !off('shadows');
  },
  get specular(): boolean {
    return !safeMode() && !off('specular');
  },
  get pixelate(): boolean {
    return !safeMode() && !off('pixelate');
  },
  /**
   * The painted `.glb` bodies for the player and shop vendors, instead of the box rig.
   *
   * **Default on since 2026-09-25** (owner's call, on the painted bodies arriving). It was off from
   * 2026-08-13 — *bring back the cube character for now but keep the code* — because the art was
   * unfinished: no armour, no UVs, one flat material below the neck. There is now one painted body
   * per chest class; see `architecture/decisions/0017-a-chest-class-is-a-whole-body.md`. The box
   * rig stays underneath and is still the fallback for every load failure.
   *
   * **Still a dev flag, not a player setting** — chosen, not specified: helmets do not show on a
   * painted body yet, so an options toggle would be offering a choice between two unfinished
   * looks. `?skinned=0` brings the boxes back for a comparison or a bug report. With it off the
   * bodies are never fetched, which is 3.8 MB.
   *
   * Reads `!off()` like every default-on switch above, which is the whole change: `on()` made it
   * opt-in, and an absent flag now means *on*.
   */
  get skinnedBodies(): boolean {
    return !safeMode() && !off('skinned');
  },
  /**
   * Monsters' painted bodies (Phase 28.6, ADR 0019), on their own: `?mobskins=0` gives every
   * monster its procedural rig while players keep theirs — the lever if a full pack costs more on
   * real hardware than the triangle and draw-call count says. Off with `?skinned=0` too, and then
   * nothing is fetched (2 MB).
   */
  /**
   * Point lights from torches, braziers and street lamps (`zone/torchlight.ts`, 2026-10-01): four
   * lights, given each frame to the sources nearest the camera. **On by default**; `?torchlight=0`
   * turns it off, and safe mode does, since it raises every material to six lights.
   */
  get torchlight(): boolean {
    return this.specular && !off('torchlight');
  },
  get mobSkins(): boolean {
    return this.skinnedBodies && !off('mobskins');
  },
  /**
   * The generated props — the town's stations, vendors, lamps and portal arch, the dungeon's
   * stairway — instead of the procedural geometry they replace. Phase 42,
   * `architecture/GENERATED-PROPS.md`. **On by default**; `?props=0` is the comparison and the
   * bug-report switch, as `?skinned=0` is. Off, nothing is fetched (2.5 MB).
   */
  get props(): boolean {
    return !safeMode() && !off('props');
  },
  /**
   * The colour grade and vignette (`grading.ts`, `AUDIO-VISUAL-EFFECTS.md` §5.5) — *can* it run.
   * Whether it *does* is the player's `Settings.grading`. Its own flag for the reason `pixelate`
   * has one: a post-process is the thing to rule out in one reload when a scene comes back wrong.
   */
  get grading(): boolean {
    return !safeMode() && !off('grade');
  },
  /**
   * The silhouette hover outline (`outline.ts`) — `?outline=0` and safe mode fall back to Babylon's
   * mesh outline, which is what hovering drew before 2026-09-27.
   */
  get outline(): boolean {
    return !safeMode() && !off('outline');
  },
  /**
   * Bloom on the surfaces that are meant to be light — windows, trim, portals.
   *
   * Its own flag as well as safe mode's, for the reason `pixelate` has one: it is a
   * separate render pass with three shaders of its own, so it is a thing to rule out in one
   * reload when a scene comes back wrong.
   */
  get glow(): boolean {
    return !safeMode() && !off('glow');
  },
  /**
   * Procedural surface textures on the floor and walls.
   *
   * Off in safe mode along with everything else: a texture adds a sampler and a
   * define to the generated shader, and `?safe=1` exists to reach the plainest
   * material Babylon will compile. `?textures=0` turns them off on their own,
   * which is the first thing to try if a zone renders black.
   */
  get textures(): boolean {
    return !safeMode() && !off('textures');
  },
};

/**
 * `?enter=1` — skip the title screen and connect at boot.
 *
 * A capture tool cannot press ENTER: `--screenshot` shoots and exits without
 * interacting, and driving the page over CDP instead means using
 * `Page.captureScreenshot`, which does not composite the WebGL canvas under
 * SwiftShader and returns a black frame. So verifying anything about the *world*
 * needs a way in that is not a click.
 *
 * Debug only, and it does not change the rule it appears to break: the title
 * screen still gates the connection for every real player, and audio still waits
 * for a gesture — a session entered this way is silent, which is correct.
 */
export function autoEnter(): boolean {
  const v = flag('enter');
  return v === '1' || v === 'true';
}

/** Logs which features are active, so a screenshot is enough to tell. */
export function logRenderFeatures(): void {
  const f = renderFeatures;
  console.info(
    `grind: render features — fog:${f.fog} shadows:${f.shadows} specular:${f.specular} ` +
      `pixelate:${f.pixelate} glow:${f.glow} textures:${f.textures}` +
      (safeMode() ? ' (SAFE MODE)' : ''),
  );
}
